import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import NotFound from '../pages/NotFound';

// Lazy load components
const HomePage = lazy(() => import('../pages/home/page'));
const NewsPage = lazy(() => import('../pages/news/page'));
const NewsDetailPage = lazy(() => import('../pages/news/[id]/page'));
const FactCheckPage = lazy(() => import('../pages/fact-check/page'));
const AnalysisPage = lazy(() => import('../pages/analysis/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const TeamPage = lazy(() => import('../pages/team/page'));
const MethodologyPage = lazy(() => import('../pages/methodology/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const CookiesPage = lazy(() => import('../pages/cookies/page'));
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminArticlesNewPage = lazy(() => import('../pages/admin/articles/new/page'));
const AdminArticlesEditPage = lazy(() => import('../pages/admin/articles/edit/[id]/page'));
const AdminPagesPage = lazy(() => import('../pages/admin/pages/page'));
const AdminPagesNewPage = lazy(() => import('../pages/admin/pages/new/page'));
const AdminPagesEditPage = lazy(() => import('../pages/admin/pages/edit/[id]/page'));
const AdminMediaPage = lazy(() => import('../pages/admin/media/page'));
const AdminUsersPage = lazy(() => import('../pages/admin/users/page'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-[#0066FF] border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm text-gray-600">Зареждане...</p>
    </div>
  </div>
);

// Wrapper component for lazy loaded routes
const LazyRoute = ({ Component }: { Component: React.LazyExoticComponent<() => JSX.Element> }) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: '/',
    element: <LazyRoute Component={HomePage} />,
  },
  {
    path: '/news',
    element: <LazyRoute Component={NewsPage} />,
  },
  {
    path: '/news/:id',
    element: <LazyRoute Component={NewsDetailPage} />,
  },
  {
    path: '/fact-check',
    element: <LazyRoute Component={FactCheckPage} />,
  },
  {
    path: '/analysis',
    element: <LazyRoute Component={AnalysisPage} />,
  },
  {
    path: '/about',
    element: <LazyRoute Component={AboutPage} />,
  },
  {
    path: '/team',
    element: <LazyRoute Component={TeamPage} />,
  },
  {
    path: '/methodology',
    element: <LazyRoute Component={MethodologyPage} />,
  },
  {
    path: '/contact',
    element: <LazyRoute Component={ContactPage} />,
  },
  {
    path: '/terms',
    element: <LazyRoute Component={TermsPage} />,
  },
  {
    path: '/privacy',
    element: <LazyRoute Component={PrivacyPage} />,
  },
  {
    path: '/cookies',
    element: <LazyRoute Component={CookiesPage} />,
  },
  {
    path: '/admin/login',
    element: <LazyRoute Component={AdminLoginPage} />,
  },
  {
    path: '/admin/dashboard',
    element: <LazyRoute Component={AdminDashboardPage} />,
  },
  {
    path: '/admin/articles/new',
    element: <LazyRoute Component={AdminArticlesNewPage} />,
  },
  {
    path: '/admin/articles/edit/:id',
    element: <LazyRoute Component={AdminArticlesEditPage} />,
  },
  {
    path: '/admin/pages',
    element: <LazyRoute Component={AdminPagesPage} />,
  },
  {
    path: '/admin/pages/new',
    element: <LazyRoute Component={AdminPagesNewPage} />,
  },
  {
    path: '/admin/pages/edit/:id',
    element: <LazyRoute Component={AdminPagesEditPage} />,
  },
  {
    path: '/admin/media',
    element: <LazyRoute Component={AdminMediaPage} />,
  },
  {
    path: '/admin/users',
    element: <LazyRoute Component={AdminUsersPage} />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
